import type { ResultSet } from '@cubejs-client/core';
import type { EChartsOption } from 'echarts';
import {
  tooltipCurrency,
  tooltipNumber,
  tooltipPercent,
} from '@shared/formatters';
import { imageState, trackImage } from '@shared/chart/tooltip-image-cache';
import { parseFirstImageUrl } from '../../../../utils/image.utils';
import type { ResolvedSlot } from './dimension.types';

/**
 * Tipo de formatação aplicado ao valor de uma métrica no tooltip.
 *
 * - `number`   → `tooltipNumber(v, decimals?)` — quantidade com separadores.
 * - `currency` → `tooltipCurrency(v)` — monetário em R$ (2 decimais).
 * - `percent`  → `tooltipPercent(v, decimals?)` — assume já em "porcento".
 * - `integer`  → `tooltipNumber(v, 0)` — inteiro com separadores.
 * - `days`     → `tooltipNumber(v, decimals ?? 1)` + sufixo `unitSuffix`.
 */
export type FormatKind = 'number' | 'currency' | 'percent' | 'days' | 'integer';

/** Especificação declarativa de uma métrica no tooltip. */
export interface TooltipMetricSpec {
  /** Chave do measure no `resultSet`/data (ex.: `'FactSales.avgSalePrice'`). */
  key: string;
  /** Chave i18n ou label literal — resolvida via `translate` com fallback à própria chave. */
  labelKey: string;
  /**
   * Label resolvido em runtime a partir dos params do ECharts. Tem precedência
   * sobre `labelKey`. Útil para tooltips onde o label depende do ponto clicado
   * (ex.: Heatmap usa `xLabels[xi]` como label da métrica).
   */
  dynamicLabel?: (params: unknown) => string;
  /**
   * Valor resolvido em runtime a partir dos params do ECharts. Tem precedência
   * sobre `row[key]`. Útil quando o valor não está na row pivotada (ex.: Heatmap
   * lê de `params.data[2]`).
   */
  dynamicValue?: (params: unknown) => unknown;
  /** Tipo de formatação. Ignorado se `customFormatter` for fornecido. */
  format: FormatKind;
  /** Casas decimais (number/percent/days). Default por tipo. */
  decimals?: number;
  /** Sufixo opcional após o número formatado (ex.: `'dias'`, `'kg'`, `'un.'`). */
  unitSuffix?: string;
  /**
   * Como obter o marker (bolinha colorida) da série.
   * - `'auto'` (default): tenta resolver pelo `markerMap` (axis multi-série); fallback ao marker do índice.
   * - `'none'`: nunca exibe marker.
   * - `'fromSeries'`: usa `params.marker` direto (útil em `trigger: 'item'`).
   */
  marker?: 'auto' | 'none' | 'fromSeries';
  /**
   * Formatter custom — substitui completamente o pipeline `format`/`decimals`/`unitSuffix`.
   * Reservado para migração das factories legadas e casos extraordinários.
   */
  customFormatter?: (value: unknown) => string;
}

/** Especificação declarativa de um tooltip de chart. */
export interface ChartTooltipSpec {
  /** Modo do tooltip — `'axis'` para Bar/Line/Heatmap; `'item'` para Scatter/Pie. */
  trigger: 'axis' | 'item';
  /** Título do tooltip. */
  title?: {
    /** Campo da row de onde o título é lido (axis trigger). */
    sourceKey?: string;
    /** Texto literal ou chave i18n. Tem precedência sobre `sourceKey`. */
    literalKey?: string;
    /**
     * Título resolvido em runtime a partir dos params do ECharts. Tem precedência
     * sobre `literalKey` e `sourceKey`. Útil para casos como Heatmap onde o
     * título depende do índice do ponto clicado.
     */
    dynamic?: (params: unknown) => string;
  };
  /** Imagem opcional acima das métricas. */
  image?: {
    /** Campo da row contendo a URL (suporta string, array, JSON-serializado). */
    sourceKey: string;
    /** Largura mínima em pixels. Default: 160. */
    size?: number;
  };
  /** Métricas exibidas no corpo do tooltip, na ordem declarada. */
  metrics: TooltipMetricSpec[];
}

/** Contexto de runtime necessário para construir o tooltip. */
export interface TooltipBuildContext {
  /** ResultSet do Cube — usado para construir `Map<dimValue, fullRow>` em `trigger: 'axis'`. */
  resultSet?: ResultSet;
  /** Slot `'grouping'` resolvido — usado para identificar o campo da dim ativa. */
  groupingSlot?: ResolvedSlot;
  /** Função de tradução (ex.: `(k) => translate.instant(k)`). */
  translate: (key: string) => string;
}

/**
 * Constrói um objeto `tooltip` para `EChartsOption` a partir de uma especificação
 * declarativa. Centraliza a aparência visual e a lógica de enriquecimento de
 * params (`Map<dimValue, fullRow>`) anteriormente espalhada por charts dimensionais.
 */
export function buildTooltip(
  spec: ChartTooltipSpec,
  context: TooltipBuildContext,
): EChartsOption['tooltip'] {
  const { trigger, image, metrics, title } = spec;
  const { resultSet, groupingSlot, translate } = context;

  const activeField =
    trigger === 'axis'
      ? (groupingSlot?.selectedDimension?.field ?? null)
      : null;

  const titleSourceKey =
    title?.sourceKey ??
    groupingSlot?.selectedDimension?.tooltip?.titleField ??
    activeField ??
    null;

  const imageSourceKey =
    image?.sourceKey ??
    groupingSlot?.selectedDimension?.tooltip?.imageField ??
    null;

  const imageSize = image?.size ?? 160;

  const resolvedLabels = metrics.map((m) =>
    resolveLabel(translate, m.labelKey),
  );
  const resolvedTitleLiteral = title?.literalKey
    ? resolveLabel(translate, title.literalKey)
    : null;

  const rowByValue = new Map<string, Record<string, unknown>>();
  if (trigger === 'axis' && resultSet && activeField) {
    for (const row of resultSet.tablePivot()) {
      const value = row[activeField] as string;
      if (value) rowByValue.set(value, row as Record<string, unknown>);
    }
  }

  const valueFormatters = metrics.map((m) => buildMetricFormatter(m));

  const dynamicTitle = title?.dynamic;

  const axisFormatter = (params: unknown): string => {
    const paramsArray = Array.isArray(params) ? params : [params];
    const first = (paramsArray[0] ?? {}) as Record<string, unknown>;

    const dimValue = String(
      first['name'] ?? first['axisValueLabel'] ?? first['axisValue'] ?? '',
    );
    const fromMap = rowByValue.get(dimValue);
    const inlineData =
      first['data'] &&
      typeof first['data'] === 'object' &&
      !Array.isArray(first['data'])
        ? (first['data'] as Record<string, unknown>)
        : null;
    const fullRow = (fromMap ?? inlineData ?? {}) as Record<string, unknown>;

    const markerMap = buildMarkerMap(paramsArray);

    const titleText =
      dynamicTitle?.(first) ??
      resolvedTitleLiteral ??
      (titleSourceKey ? String(fullRow[titleSourceKey] ?? '') : '');

    const imageUrl = imageSourceKey
      ? parseFirstImageUrl(fullRow[imageSourceKey])
      : null;

    return renderTooltipHtml({
      titleText,
      imageUrl,
      imageSize,
      rows: metrics.map((m, i) => ({
        marker: resolveMarker(m, paramsArray, markerMap, i, 'axis'),
        label: m.dynamicLabel?.(first) ?? resolvedLabels[i],
        value: valueFormatters[i](
          m.dynamicValue ? m.dynamicValue(first) : fullRow[m.key],
        ),
      })),
    });
  };

  const itemFormatter = (params: unknown): string => {
    const p = (params ?? {}) as Record<string, unknown>;
    const dataField = p['data'];
    const isObjectData =
      dataField != null &&
      typeof dataField === 'object' &&
      !Array.isArray(dataField);
    const dataObj = isObjectData
      ? (dataField as Record<string, unknown>)
      : null;
    const row = (dataObj?.['_row'] ?? dataObj ?? {}) as Record<string, unknown>;

    const titleText =
      dynamicTitle?.(p) ??
      resolvedTitleLiteral ??
      (titleSourceKey
        ? String(row[titleSourceKey] ?? p['name'] ?? '')
        : String(p['name'] ?? ''));

    const imageUrl = imageSourceKey
      ? parseFirstImageUrl(row[imageSourceKey])
      : null;

    return renderTooltipHtml({
      titleText,
      imageUrl,
      imageSize,
      rows: metrics.map((m, i) => ({
        marker: resolveMarker(m, [p], new Map(), i, 'item'),
        label: m.dynamicLabel?.(p) ?? resolvedLabels[i],
        value: valueFormatters[i](
          m.dynamicValue ? m.dynamicValue(p) : row[m.key],
        ),
      })),
    });
  };

  return {
    show: true,
    trigger,
    ...(trigger === 'axis' && { axisPointer: { type: 'shadow' as const } }),
    formatter: trigger === 'axis' ? axisFormatter : itemFormatter,
  };
}

function resolveLabel(translate: (key: string) => string, key: string): string {
  // Chave vazia (ex.: métrica com `dynamicLabel`) não tem tradução e faria o
  // TranslateService.instant('') lançar — devolve a própria chave sem traduzir.
  if (!key) return key;
  const translated = translate(key);
  return translated && translated !== key ? translated : key;
}

function buildMetricFormatter(m: TooltipMetricSpec): (v: unknown) => string {
  if (m.customFormatter) return m.customFormatter;
  const base = (() => {
    switch (m.format) {
      case 'currency':
        return (v: unknown) => (v != null ? tooltipCurrency(Number(v)) : '—');
      case 'percent':
        return (v: unknown) =>
          v != null ? tooltipPercent(Number(v), m.decimals) : '—';
      case 'integer':
        return (v: unknown) => (v != null ? tooltipNumber(Number(v), 0) : '—');
      case 'days':
        return (v: unknown) =>
          v != null ? tooltipNumber(Number(v), m.decimals ?? 1) : '—';
      case 'number':
      default:
        return (v: unknown) =>
          v != null ? tooltipNumber(Number(v), m.decimals) : '—';
    }
  })();
  if (m.unitSuffix) {
    const suffix = m.unitSuffix;
    return (v) => `${base(v)} ${suffix}`;
  }
  return base;
}

function buildMarkerMap(paramsArray: unknown[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const raw of paramsArray) {
    const p = raw as Record<string, unknown>;
    const marker = p?.['marker'];
    const seriesName = p?.['seriesName'];
    if (typeof marker !== 'string' || typeof seriesName !== 'string') continue;
    const dimensionNames = p['dimensionNames'] as string[] | undefined;
    const encode = p['encode'] as { x?: number[]; y?: number[] } | undefined;
    const encodedKey =
      dimensionNames?.[encode?.x?.[0] ?? -1] ??
      dimensionNames?.[encode?.y?.[0] ?? -1];
    if (encodedKey) map.set(encodedKey, marker);
    map.set(seriesName, marker);
  }
  return map;
}

function resolveMarker(
  m: TooltipMetricSpec,
  paramsArray: unknown[],
  markerMap: Map<string, string>,
  index: number,
  trigger: 'axis' | 'item',
): string {
  const mode = m.marker ?? (trigger === 'axis' ? 'auto' : 'none');
  if (mode === 'none') return '';
  if (mode === 'fromSeries') {
    return String(
      (paramsArray[0] as Record<string, unknown>)?.['marker'] ?? '',
    );
  }
  const fromMap = markerMap.get(m.key);
  if (fromMap) return fromMap;
  const fromIndex = (
    paramsArray[index] as Record<string, unknown> | undefined
  )?.['marker'];
  return typeof fromIndex === 'string' ? fromIndex : '';
}

interface RenderInput {
  titleText: string;
  imageUrl: string | null;
  imageSize: number;
  rows: Array<{ marker: string; label: string; value: string }>;
}

/**
 * Bloco da foto de produto num tooltip de gráfico. Exportado porque os relatórios de Sucesso
 * montam o tooltip por conta própria e precisam do MESMO comportamento — a regra abaixo é sutil
 * demais para ser reescrita em cada lugar.
 *
 * O ECharts chama o `formatter` a cada mousemove, não a cada hover: o HTML é remontado e o `<img>`
 * é um elemento novo dezenas de vezes por segundo. Por isso o desfecho de cada URL fica em
 * memória (<see cref="tooltip-image-cache"/>) e decide o HTML antes de montá-lo:
 *
 * - **conhecida boa**: sai já visível, sem `onload`. É o que mata o flick — o frame seguinte não
 *   recomeça do estado oculto.
 * - **conhecida ausente**: não sai. Nada de moldura, nada de nova requisição para um 404 sabido.
 * - **desconhecida**: sai oculta com `onload` que revela, e a sondagem começa em paralelo. Cobre
 *   o caso do mouse parado, em que não haveria frame seguinte para aproveitar o cache.
 *
 * Esconde a MOLDURA, não o `<img>`: ela tem `aspect-ratio: 1/1` e sozinha deixaria um retângulo
 * vazio. Por estilo em vez de `remove()` porque o nó pertence à árvore que o ECharts gerencia. E
 * `display` é seguro aqui porque a imagem do tooltip não é lazy.
 */
export function renderTooltipImage(
  imageUrl: string | null,
  altText: string,
): string {
  if (!imageUrl) return '';

  const state = imageState(imageUrl);
  if (state === 'failed') return '';
  if (state === 'unknown') trackImage(imageUrl);

  const oculta = state === 'loaded' ? '' : 'display: none; ';
  const revela =
    state === 'loaded' ? '' : ` onload="this.parentElement.style.display=''"`;

  return `<div style="${oculta}position: relative; aspect-ratio: 1 / 1; border-radius: 8px; overflow: hidden; margin-bottom: 8px;">
         <img src="${imageUrl}" alt="${altText}" style="position: absolute; width: 100%; height: 100%; object-fit: contain;" class="bg-fundo-900/10"${revela} />
       </div>`;
}

function renderTooltipHtml(input: RenderInput): string {
  const { titleText, imageUrl, imageSize, rows } = input;
  const metricsRows = rows
    .map(
      (r) => `<tr>
        <td style="color: var(--chart-textColor); padding-right: 12px;">${r.marker} ${r.label}</td>
        <td style="font-weight: bold; text-align: right;">${r.value}</td>
      </tr>`,
    )
    .join('');
  const metricsHtml = `<table style="border-collapse: collapse; width: 100%;"><tbody>${metricsRows}</tbody></table>`;
  const imageHtml = renderTooltipImage(imageUrl, titleText);
  const titleHtml = titleText
    ? `<div style="font-weight: bold; margin-bottom: 4px;">${titleText}</div>`
    : '';
  return `
    <div style="display: flex; flex-direction: column; color: var(--chart-tooltip-fg); min-width: min(${imageSize}px, 100%); max-width: 100%;">
      ${imageHtml}
      <div>
        ${titleHtml}
        ${metricsHtml}
      </div>
    </div>
  `;
}

/**
 * Tooltip "auto-from-series" — filtra séries com valor 0/null/NaN e formata
 * cada valor visível com o `valueFormatter`. Usado pelos templates
 * BarChart/PieChart/LineChart no caminho default, quando as métricas
 * são determinadas dinamicamente pelo ECharts (não declaradas estaticamente).
 *
 * Lida com os 3 formatos de `params.value` que aparecem na app:
 * - Número: barras single-/multi-série com `data: [N, N, ...]`.
 * - Tupla `[x, y]`: line chart time-series com `data: [[ts, v], ...]`.
 * - Objeto `{ value, ... }`: pie ou charts com data rica.
 */
export function buildSeriesTooltip(options: {
  valueFormatter: (v: unknown) => string;
  trigger?: 'axis' | 'item';
}): EChartsOption['tooltip'] {
  const { valueFormatter, trigger = 'axis' } = options;

  const formatter = (params: unknown): string => {
    const arr = Array.isArray(params)
      ? (params as Array<Record<string, unknown>>)
      : [params as Record<string, unknown>];

    const visible = arr
      .map((p) => ({ p, n: numericValue(p) }))
      .filter(({ n }) => n !== null && n !== 0)
      .sort((a, b) => (b.n as number) - (a.n as number));
    if (visible.length === 0) return '';

    const first = arr[0] ?? {};
    const title = String(first['axisValueLabel'] ?? first['name'] ?? '');

    const rows = visible.map(({ p, n }) => {
      const label = String(p['seriesName'] ?? p['name'] ?? '');
      const marker = (p['marker'] as string | undefined) ?? '';
      return `${marker}<span style="margin-left:4px;">${label}</span>: <b style="margin-left:8px;">${valueFormatter(n)}</b>`;
    });
    return `<div style="font-weight:600;margin-bottom:4px;">${title}</div>${rows.join('<br/>')}`;
  };

  return {
    show: true,
    trigger,
    formatter,
  };
}

function numericValue(p: Record<string, unknown>): number | null {
  const v = p['value'];
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (Array.isArray(v)) {
    const last = Number(v[v.length - 1]);
    return Number.isNaN(last) ? null : last;
  }
  if (v && typeof v === 'object' && 'value' in (v as Record<string, unknown>)) {
    const n = Number((v as Record<string, unknown>)['value']);
    return Number.isNaN(n) ? null : n;
  }
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}
